import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid2";
import {
  Alert,
  Stack,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useSearchParams } from "react-router-dom";
import {
  ADVISOR_GAP_RULE_NOTE,
  ADVISOR_GAP_RULE_PLACEHOLDER,
  ADVISOR_MAP_DETAILS_DEFAULTS,
  advisorAssignedGapRules,
  advisorCanonicalMetrics,
  getAdvisorGapRuleById,
} from "../helpers/advisorStaticData";
import "../Advisor.css";

type SortDirection = "asc" | "desc";

const tableHeaders = [
  { id: 0, KEY: "metric", label: "Metric", sortable: true },
  { id: 1, KEY: "gapRule", label: "Gap Rule", sortable: true },
  { id: 2, KEY: "derivations", label: "Derivations", sortable: true },
  { id: 3, KEY: "dependsOn", label: "Depends On", sortable: true },
];

const AdvisorMapDetails = () => {
  const [searchParams] = useSearchParams();
  const [sort, setSort] = React.useState<{
    keytoSort: string;
    direction: SortDirection;
  }>({ keytoSort: "", direction: "asc" });

  const mapName =
    searchParams.get("mapName") || ADVISOR_MAP_DETAILS_DEFAULTS.mapName;
  const interfaceName =
    searchParams.get("interface") || ADVISOR_MAP_DETAILS_DEFAULTS.interface;
  const destinationTable =
    searchParams.get("destinationTableMain") ||
    ADVISOR_MAP_DETAILS_DEFAULTS.destinationTable;

  /** the gap rule assigned to each canonical metric on this map */
  const mapDetailsRows = React.useMemo(
    () =>
      advisorCanonicalMetrics.map((metric) => ({
        metric,
        gapRule: getAdvisorGapRuleById(advisorAssignedGapRules[metric] ?? null),
      })),
    [],
  );

  const handleHeaderClick = (header: (typeof tableHeaders)[number]) => {
    if (!header.sortable) return;
    setSort((prev) => ({
      keytoSort: header.KEY,
      direction:
        prev.keytoSort === header.KEY && prev.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const sortedRows = React.useMemo(() => {
    if (!sort.keytoSort) return mapDetailsRows;

    const getSortValue = (row: (typeof mapDetailsRows)[number]) => {
      if (sort.keytoSort === "gapRule") return row.gapRule?.label ?? "";
      if (sort.keytoSort === "derivations")
        return row.gapRule?.expression ?? "";
      if (sort.keytoSort === "dependsOn")
        return row.gapRule?.dependsOn.join(", ") ?? "";
      return row.metric;
    };

    return mapDetailsRows.slice().sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);

      // rows without a gap rule / derivation always sink to the bottom
      if (!aValue) return 1;
      if (!bValue) return -1;

      return sort.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  }, [mapDetailsRows, sort]);

  const derivedByRuleCount = mapDetailsRows.filter((row) => row.gapRule).length;

  return (
    <Paper sx={{ mt: 2 }}>
      <Box sx={{ padding: "8px 16px" }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Typography variant="h3" sx={{ fontSize: "24px" }}>
              {`${mapName} Map Details`}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              gap={2}
            >
              <TextField
                label="Interface"
                value={interfaceName}
                size="small"
                fullWidth
                slotProps={{ input: { readOnly: true } }}
              />
              <TextField
                label="Destination Table"
                value={destinationTable}
                size="small"
                fullWidth
                slotProps={{ input: { readOnly: true } }}
              />
            </Stack>
          </Grid>
        </Grid>

        <Alert
          severity="warning"
          icon={<ErrorOutlineIcon fontSize="small" />}
          sx={{
            mb: 1,
            color: "warning.main",
            "& .MuiAlert-icon": { color: "warning.main", alignItems: "center" },
            "& .MuiAlert-message": { fontWeight: 500, fontSize: "14px" },
          }}
        >
          {ADVISOR_GAP_RULE_NOTE}
        </Alert>
      </Box>

      <TableContainer className="advisorMapDetailsTable">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header) => (
                <TableCell key={header.id} align="left">
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows && sortedRows.length > 0 ? (
              sortedRows.map((row) => (
                <TableRow
                  key={row.metric}
                  sx={{
                    "& > *": {
                      borderBottom:
                        "1px solid rgba(224, 224, 224, 1) !important",
                      padding: "10px 16px",
                    },
                  }}
                >
                  <TableCell align="left">{row.metric}</TableCell>
                  <TableCell align="left">
                    {row.gapRule?.label || ADVISOR_GAP_RULE_PLACEHOLDER}
                  </TableCell>
                  <TableCell align="left">
                    {row.gapRule ? (
                      <>
                        <Typography component="span" sx={{ fontSize: "14px" }}>
                          {row.gapRule.expression}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{ fontSize: "14px", color: "text.secondary" }}
                        >
                          {`: ${row.gapRule.description}`}
                        </Typography>
                      </>
                    ) : (
                      <Typography
                        component="span"
                        sx={{ fontSize: "14px", color: "text.secondary" }}
                      >
                        from source
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="left">
                    {row.gapRule && row.gapRule.dependsOn.length > 0
                      ? row.gapRule.dependsOn.join(", ")
                      : "-"}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={tableHeaders.length} align="center">
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ padding: "8px 16px" }}>
        <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
          {`${derivedByRuleCount} of ${mapDetailsRows.length} metrics derived by a rule`}
        </Typography>
      </Box>
    </Paper>
  );
};

export default AdvisorMapDetails;
